import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import { isBefore, isEqual, parseISO, startOfDay } from 'date-fns';
import { MatchPeriodFilter, MatchSort, MatchStatus, MatchStatusFilter, Club } from '@repo/types';
import { ClubNameType } from '@ui/fixtures/contracts';
import FixtureListUI from './FixtureListUI';
import { resolveFixtureListLinkUrl } from './resolveLinkUrl';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useMemo } from 'react';

export type FixtureListProps = {
  groupByDate: boolean;
  clubFromSlug?: boolean;
  className?: string;
  period?: MatchPeriodFilter;
  periodLimit?: number;
  status?: MatchStatusFilter;
  limit?: number;
  sort?: MatchSort;
  from?: string;
  to?: string;
  competitionId?: string;
  clubId?: string;
  teamId?: string;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  columns?: number;
  linkLabel?: string;
  linkUrl?: string;
  linkTargetBlank?: boolean;
  linkVariant?: LinkVariant;
  linkSize?: LinkSize;
  title?: string;
  extraData?: {
    club?: Club;
  };
  autoPolling?: boolean;
};

const POLLING_INTERVAL = 60 * 1000; // 60 seconds

export function FixtureList({
  groupByDate,
  clubFromSlug,
  extraData,
  className,
  period,
  periodLimit,
  limit,
  sort,
  status,
  from,
  to,
  competitionId,
  clubId,
  teamId,
  nameType,
  style,
  columns,
  linkLabel,
  linkUrl,
  linkTargetBlank,
  // SC-1143: must match FixtureListUI's default. This wrapper forwards `linkVariant`
  // unconditionally, so its own default — not the child's — is what a CMS page that never set
  // the field actually renders.
  linkVariant = LinkVariant.Bare,
  linkSize = LinkSize.m,
  title,
  autoPolling = false,
}: FixtureListProps) {
  const firstTeam = extraData?.club?.teams?.find((team) => team.isDefault);
  const { data: fixtures, isLoading } = useMatchesQuery(
    {
      competition: competitionId ?? '',
      teamId: clubFromSlug && firstTeam ? firstTeam.id : (teamId ?? ''),
      period,
      periodLimit,
      startDate: period === MatchPeriodFilter.Custom && from ? from : '',
      endDate: period === MatchPeriodFilter.Custom && to ? to : '',
      limit,
      sort,
      status,
    },
    {
      refetchInterval: autoPolling ? POLLING_INTERVAL : false,
    }
  );

  const matchesWithoutPostponed = useMemo(() => {
    const today = new Date();
    return (
      fixtures?.items.filter((match) => {
        if (match.status !== MatchStatus.Postponed) {
          return true;
        }

        const matchDate = parseISO(match.date);

        if (isBefore(startOfDay(today), startOfDay(matchDate))) {
          return true;
        } else if (isEqual(startOfDay(today), startOfDay(matchDate))) {
          return true;
        } else {
          return false;
        }
      }) ?? []
    );
  }, [fixtures?.items]);

  const resolvedLinkUrl = resolveFixtureListLinkUrl({
    competitionId,
    clubId,
    clubFromSlug,
    periodLimit,
    linkUrl,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={21} />
      </div>
    );
  }

  return (
    <FixtureListUI
      fixtures={matchesWithoutPostponed}
      groupByDate={groupByDate}
      nameType={nameType}
      columns={columns}
      className={className}
      style={style}
      linkLabel={linkLabel}
      linkUrl={resolvedLinkUrl}
      linkTargetBlank={linkTargetBlank}
      linkVariant={linkVariant}
      linkSize={linkSize}
      title={title}
      sortByDate={sort ? (sort.toUpperCase() as 'ASC' | 'DESC') : 'DESC'}
    />
  );
}
